import { getVisibleMethods, hasMethod } from "../../modules/recipientDrawer";

import type { RecipientPaymentMethod } from "../../modules/recipientDrawer";

type EnabledMethods = Record<RecipientPaymentMethod, boolean>;

// what a recipient row says about its payment methods; the caller localizes it
export type RecipientPaymentMethodsSummary =
  | { kind: "all" }
  | { kind: "none" }
  | { kind: "some"; methods: RecipientPaymentMethod[] };

export const getPaymentMethodsSummary = (
  recipient: API.Recipient,
  enabledMethods: EnabledMethods,
): RecipientPaymentMethodsSummary => {
  const visibleMethods = getVisibleMethods(enabledMethods);
  const methods = visibleMethods.filter((method) =>
    hasMethod(recipient, method),
  );

  if (methods.length === 0) {
    return { kind: "none" };
  }
  // with a single method enabled, naming it beats "All payment methods"
  if (visibleMethods.length > 1 && methods.length === visibleMethods.length) {
    return { kind: "all" };
  }
  return { kind: "some", methods };
};

export interface RecipientListFilters {
  search?: string;
  // a recipient matches when it has any of these
  methods?: RecipientPaymentMethod[];
}

const matchesSearch = (recipient: API.Recipient, query: string) =>
  [recipient.name, recipient.nickname].some((value) =>
    value?.toLocaleLowerCase().includes(query),
  );

export const filterRecipients = (
  recipients: API.Recipient[],
  { search = "", methods = [] }: RecipientListFilters = {},
) => {
  const query = search.trim().toLocaleLowerCase();

  return recipients.filter(
    (recipient) =>
      (!query || matchesSearch(recipient, query)) &&
      (methods.length === 0 ||
        methods.some((method) => hasMethod(recipient, method))),
  );
};

// the wire methods a recipient has, in display order. International wires only
// count when the user can send them
export const getRecipientWireMethods = (
  recipient: API.Recipient,
  { hasInternationalWires }: { hasInternationalWires: boolean },
): RecipientPaymentMethod[] =>
  getVisibleMethods({
    ach: false,
    wire: true,
    internationalWire: hasInternationalWires,
  }).filter((method) => hasMethod(recipient, method));
